import { zodResolver } from "@hookform/resolvers/zod";
import { Control, useForm, UseFormHandleSubmit, UseFormReturn } from "react-hook-form";
import { any, record, z } from "zod";

import { useT } from "src/internationalization";
import { operatorCodeMapping } from "src/mappers/operator.mappers";
import { ClientSchemaBlacklist, ClientSchemaIdentifier } from "src/types/clientSchema";
import { OperatorCode, OperatorValueType } from "src/types/operator";
import { Property } from "src/types/property";
import { AppDataType } from "src/types/typedValue";

const propertySchema = z.object({
  code: z.string(),
  dataType: z.custom<AppDataType>(),
  confListCode: z.string().nullable(),
});

export type RuleDefinitionControl = {
  control: Control<RuleFormData>;
  submit: UseFormHandleSubmit<RuleFormData>;
  hasError: boolean;
  methods: UseFormReturn<RuleFormData>;
  criteria: RuleFormData["criteria"];
};
export type RuleFormData = {
  criteria: {
    property: Pick<Property, "code" | "dataType" | "confListCode"> | null;
    operator: { code: OperatorCode; valueType: OperatorValueType } | null;
    value: string;
  }[];
  listElementType: ClientSchemaBlacklist | null;
  identifier: ClientSchemaIdentifier | null;
};
const useSchema = () => {
  const t = useT();
  const criterionSchema = z
    .object({
      property: propertySchema.or(z.null()),
      operator: z
        .object({
          code: z.custom<OperatorCode>((operator) =>
            Object.values(operatorCodeMapping).some((o) => o === operator)
          ),
          valueType: z.enum(["argument", "argumentList"]),
        })
        .or(z.null()),
      value: z.string(),
    })
    .refine((val) => val.property !== null, {
      message: t("page.rules.simple.form.property.required"),
      path: ["property"],
    })
    .refine((val) => val.operator !== null, {
      message: t("page.rules.simple.form.operator.required"),
      path: ["operator"],
    })
    .superRefine((data, ctx) => {
      if (data.operator && (!data.value || data.value.trim().length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("page.rules.simple.form.value.required"),
          path: ["value"],
        });
      }
    });
  return z.object({
    identifier: record(any(), { message: t("general.form.field.required") }).or(z.null()),
    listElementType: record(any(), { message: t("general.form.field.required") }),
    criteria: z.array(criterionSchema),
  });
};

export const useRuleDefinition: () => RuleDefinitionControl = () => {
  const validationSchema = useSchema();
  const methods = useForm<RuleFormData>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      identifier: null,
      listElementType: null,
      criteria: [],
    },
  });

  const criteria = methods.watch("criteria");
  const control = methods.control;

  const hasError = Object.keys(methods.formState.errors).length > 0;

  return {
    methods,
    submit: methods.handleSubmit,
    hasError,
    control,
    criteria,
  };
};
